import {
    BillingInfo,
    Customer,
    CustomerAddress,
    CustomerConsumptionStats,
    Invoice,
    PLAN_SUBSCRIPTION_STATUS,
    PlanSubscriptionWithTotal,
    ProductAvailability,
    SubscriptionExtract
} from "../../types/customer";
import Stripe from "stripe";
import BEStripe from "stripe";
import {AnyPrice, FixedPrice, Price, Product, ProductIdType} from "../../types/products";
import {getProductById} from "../../utils/commons";
import {SolutionKey} from "../../types/solutions";

export function toCustomerAddress(addr: Stripe.Address): CustomerAddress {
    return addr ? ({
        city: addr.city || undefined,
        countryISOCodeAlpha2: addr.country || undefined,
        postalCode: addr.postal_code || undefined,
        street: addr.line1 || undefined,
        province: addr.state || undefined,
    }) : {};
}

export function toStripeAddress(addr: CustomerAddress): Stripe.Address | undefined {
    return Object.keys(addr).length === 0 ? undefined : ({
        city: addr.city ||  null,
        line1: addr.street || null,
        postal_code: addr.postalCode || null,
        country: addr.countryISOCodeAlpha2 || null,
        state: addr.province || null,
        line2: null,
    });
}

export function toBillingInfo(obj: any): BillingInfo {
    return {
        address: toCustomerAddress(obj.address),
        codiceFiscale: obj.taxCode,
        email: obj.email,
        invoiceEmail: obj.invoiceEmail,
        pIVA: obj.vatNumber,
        ragioneSociale: obj.name,
    };
}

export function toCustomer(obj: any): Customer {
    return {
        id: obj.id,
        email: obj.email,
        name: obj.name,
        stripeId: obj.stripeCustomerId,
    };
}

export function toUserBillingInfo(bi: BillingInfo): any {
    return {
        email: bi.email,
        invoiceEmail: bi.invoiceEmail,
        name: bi.ragioneSociale,
        vatNumber: bi.pIVA,
        taxCode: bi.codiceFiscale,
        address: toStripeAddress(bi.address),
    };
}

export function getProductNamesFromPrices(rawPrices: Array<{id: string; product: string}>, products: Product[]) {
    return Array.from(
        rawPrices.reduce<Set<ProductIdType | "">>(
            (previousValue, currentValue) => {
                const productById = getProductById(currentValue?.product as string || "", products || []);
                previousValue.add(productById?.id || "");
                return previousValue;
            }, new Set()
        ),
    ).filter(value => !!value) as ProductIdType[];
}

export function getOneTimeFromPrices(rawPrices: Array<{id: string; product: string}>, products: Product[]) {
    return rawPrices.reduce<boolean>(
        (previousValue, currentValue) => {
            const productById = getProductById(currentValue?.product as string || "", products || []);
            const priceById: Price | undefined = productById?.prices.find((price) => price.id === currentValue.id);
            return previousValue || !!(priceById?.oneTime || false);
        }, false
    );
}

export function getSolutionNamesFromPrices(rawPrices: Array<{id: string; product: string}>, products: Product[]) {
    return Array.from(
        rawPrices.reduce<Set<ProductIdType | "">>(
            (previousValue, currentValue) => {
                const productById = getProductById(currentValue?.product as string || "", products || []);
                previousValue.add(productById?.solution || "");
                return previousValue;
            }, new Set()
        ),
    ).filter(value => !!value) as SolutionKey[];
}


export function getRawPricesFromInvoice(invoice: BEStripe.Invoice): Array<{id: string; product: string}> {
    return invoice.lines.data
        .map(value => value.pricing?.price_details)
        .filter((pd): pd is NonNullable<typeof pd> => !!pd)
        .map(pd => ({id: pd.price, product: pd.product}));
}

export function toInvoice(invoice: Invoice, products: Product[]): Invoice {
    if (invoice.invoice && products) {
        const rawPrices = getRawPricesFromInvoice(invoice.invoice);
        return {
            ...invoice,
            timestamp: new Date(+invoice.timestamp),
            solutionKeys: getSolutionNamesFromPrices(rawPrices, products),
            productNames: getProductNamesFromPrices(rawPrices, products),
        };
    } else {
        return invoice;
    }
}

function getReferenceInvoice(invoices: BEStripe.Invoice[]): BEStripe.Invoice {
    let referenceInvoice = invoices[0];
    for (const invoice of invoices) {
        if (invoice.billing_reason === "subscription_update") {
            referenceInvoice = invoice;
        }
    }
    return referenceInvoice;
}

// function getInvoicesTotal(invoices: BEStripe.Invoice[]): number {
//     return invoices.reduce((previousValue, currentValue) => previousValue + currentValue.total, 0);
// }

export function toPlanSubscriptionWithTotal(sub: (BEStripe.Subscription & {invoices: Stripe.Invoice[]}), products: Product[], customer: Customer): PlanSubscriptionWithTotal {
    const rawPrices = sub.items.data.map(item => ({
        id: item.price.id,
        product: item.price.product as string,
    }));
    const productNames = getProductNamesFromPrices(rawPrices, products);
    const solutions = getSolutionNamesFromPrices(rawPrices, products);
    // Prices belonging to addon products won't resolve to a product in the Redux state (addon products are excluded from it).
    const rawNonAddonPrices = rawPrices.filter(raw => !!getProductById(raw.product, products));
    // Collect addons from the already-resolved Product.addons, deduplicating by base product.
    const seenSids = new Set<string>();
    const addonPrices: FixedPrice[] = rawPrices
        .map(raw => getProductById(raw.product, products))
        .filter((p): p is Product => !!p && !seenSids.has(p.sid) && !!seenSids.add(p.sid))
        .flatMap(p => p.addons);
    const seenPriceIds = new Set<string>();
    const prices: AnyPrice[] = [
        ...addonPrices,
        ...rawNonAddonPrices.flatMap(raw => {
            const product = getProductById(raw.product, products);
            if (!product) return [];
            return product.prices.filter(p => {
                const firstId = (Array.isArray(p.id) ? p.id[0] : p.id) as string;
                if (seenPriceIds.has(firstId)) return false;
                if (Array.isArray(p.id) ? p.id.includes(raw.id) : p.id === raw.id) {
                    seenPriceIds.add(firstId);
                    return true;
                }
                return false;
            });
        }),
    ];
    const referenceInvoice = getReferenceInvoice(sub.invoices);
    const definitiveTotal = referenceInvoice.total / 100;
    const tax = (referenceInvoice.total_taxes?.reduce((sum, t) => sum + t.amount, 0) || 0) / 100;
    const oneTimeFromPrices = getOneTimeFromPrices(rawPrices, products);
    const status = sub.status === "active" && !oneTimeFromPrices && sub.cancel_at_period_end ? "canceled" : sub.status;
    return {
        id: sub.id,
        created: new Date(sub.created * 1000),
        cancelAt: (sub.status === PLAN_SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED || sub.status === PLAN_SUBSCRIPTION_STATUS.INCOMPLETE) ? undefined : (sub.cancel_at && new Date(sub.cancel_at * 1000)) || undefined,
        canceledAt: sub.status === PLAN_SUBSCRIPTION_STATUS.CANCELED ? (sub.canceled_at && new Date(sub.canceled_at * 1000)) || undefined : undefined,
        startDate: (sub.status === PLAN_SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED || sub.status === PLAN_SUBSCRIPTION_STATUS.INCOMPLETE) ? undefined : (sub.start_date && new Date(sub.start_date * 1000)) || undefined,
        status,
        prices,
        productNames,
        solutionKeys: solutions,
        total: definitiveTotal, // The definitive total relies on the reference invoice, which includes one time fees that are not recurring and are not representative of the subscription's recurring total.
        customer: customer,
        tax,
        cancelAtPeriodEnd: sub.cancel_at_period_end,
        nextRenew: sub.items.data[0]?.current_period_end ? new Date(sub.items.data[0].current_period_end * 1000) : undefined,
        oneTime: oneTimeFromPrices,
    };
}

export function toCustomerConsumptionStats(obj: any): CustomerConsumptionStats {
    return {
        customerId: obj.customerId,
        customerEmail: obj.customerEmail,
        customerBillingEmail: obj.customerBillingEmail,
        partner: obj.partner,
        availability: obj.availability.map((a: any) => ({
            solution: a.solution,
            productInternalId: a.productInternalId,
            productId: a.productId,
            lastConsumption: a.lastConsumption ? new Date(a.lastConsumption) : undefined,
            current: a.current,
            historical: a.historical,
            activeSubs: a.activeSubs.map((s: any) => ({
                id: s.id,
                cancel_at: s.cancel_at ? new Date(s.cancel_at * 1000) : null,
            } satisfies SubscriptionExtract)),
        } satisfies ProductAvailability)),
    };
}
